import { createSlice } from '@reduxjs/toolkit';
import ApiServices from 'api/apexchat.api.services';
import { UserAPI } from 'api/endpoints';
import {
  isObjectEmpty,
  tryParseJson,
  updateViewedUser
} from 'services/utils/generic.methods';
import storage from 'services/utils/storage';

/**
 * @name @createSlice
 * @description create redux store slice for userPreferenceSettings
 **/
export const userPreferenceSettings = createSlice({
  name: 'userPreferenceSettings',
  initialState: {
    preferences: {
      version: 0,
      analytics: {},
      general: {
        tour: false,
        language: 'en-US'
      },
      messaging: {
        enableAutoPickup: false,
        enableNotificationSound: true,
        signUpRequested: false
      },
      companies: {
        filters: {},
        columns: null
      },
      chats: {
        filters: {},
        columns: null
      },
      leads: {
        filters: {},
        columns: null
      },
      reports: {
        filters: {},
        columns: null
      },
      queue: {
        filters: {},
        columns: null
      },
      provision: {
        filters: {},
        columns: null
      },
      settings: {
        filters: {},
        columns: null
      },
      userinvite: {
        filters: {},
        columns: null
      },
      operations: {
        filters: {},
        columns: null
      }
    }
  },

  reducers: {
    setGridFilters: (state, action) => {
      state.preferences[action.payload.key].filters = {
        ...action.payload.filters
      };
    },

    setGridColumns: (state, action) => {
      state.preferences.version += 1;
      state.preferences[action.payload.key].columns = action.payload.columns;
    },

    setEnableAutoPickup: (state, action) => {
      state.preferences.version += 1;
      state.preferences.messaging.enableAutoPickup = !!action.payload;
      updateViewedUser({ ap: !!action.payload });
    },

    setEnableNotificationSound: (state, action) => {
      state.preferences.version += 1;
      state.preferences.messaging.enableNotificationSound = !!action.payload;
      updateViewedUser({ enableSound: !!action.payload });
    },

    setAnalyticsFilters: (state, action) => {
      state.preferences.version += 1;
      let filters = { ...action.payload };
      state.preferences.analytics.filters = filters;
      storage.setItem('analytics-selected-filters', filters);
    },

    setAnalyticsCharts: (state, action) => {
      state.preferences.version += 1;
      let charts = { ...action.payload };
      state.preferences.analytics.charts = charts;
      storage.setItem('analyticsChartsHideShow', charts);
    },

    setAnalyticsDuration: (state, action) => {
      state.preferences.version += 1;
      let duration = { ...action.payload };
      state.preferences.analytics.duration = duration;
      storage.setItem('duration', duration);
    },

    setAnalyticsWidgets: (state, action) => {
      state.preferences.version += 1;
      let widgets = [...action.payload];
      state.preferences.analytics.widgets = widgets;
      storage.setItem('analytics-quickstats-widget', widgets);
    },

    setLanguage: (state, action) => {
      let lang = action.payload;
      if (state.preferences.general.language !== lang) {
        state.preferences.version += 1;
        state.preferences.general.language = lang;
        storage.setItem('language', lang);
      }
    },

    setSignupRequested: (state, action) => {
      state.preferences.version += 1;
      state.preferences.messaging.signUpRequested = !!action.payload;
    },

    setPreferencesToState: (state, action) => {
      let newPreferences = { ...action.payload };
      // Set items to local storage
      const { widgets, filters, charts, duration } =
        newPreferences.analytics || {};
      const { language, tour } = newPreferences.general || {};
      const { enableAutoPickup, enableNotificationSound } =
        newPreferences.messaging || {};
      updateViewedUser({
        ap: !!enableAutoPickup,
        enableSound: !!enableNotificationSound
      });

      // Remove filters key
      Object.keys(newPreferences).forEach(categoryKey => {
        const { filters, ...rest } = newPreferences[categoryKey];
        newPreferences[categoryKey] = rest;
      });

      if (widgets && widgets.length)
        storage.setItem('analytics-quickstats-widget', widgets);
      if (charts && !isObjectEmpty(charts))
        storage.setItem('analyticsChartsHideShow', charts);
      if (filters && !isObjectEmpty(filters))
        storage.setItem('analytics-selected-filters', filters);
      if (duration && !isObjectEmpty(duration))
        storage.setItem('duration', duration);
      if (language && language !== '') storage.setItem('language', language);

      return {
        preferences: {
          ...state.preferences,
          ...newPreferences,
          version: 0
        }
      };
    }
  }
});

export const {
  setGridFilters,
  setGridColumns,
  setAnalyticsFilters,
  setAnalyticsCharts,
  setAnalyticsDuration,
  setAnalyticsWidgets,
  setLanguage,
  setSignupRequested,
  setEnableAutoPickup,
  setEnableNotificationSound,
  setPreferencesToState
} = userPreferenceSettings.actions;

export const selectLanguage = state => {
  return state.userPreferenceSettings.preferences.general.language;
};

export const getSignupRequested = state => {
  return state.userPreferenceSettings.preferences.messaging.signUpRequested;
};

export const getUserPreferenceVersion = state => {
  return state.userPreferenceSettings.preferences.version;
};

export const getUserPreferences = state => {
  return state.userPreferenceSettings.preferences;
};

export const selectGridFilters = (state, key) => {
  if (key) return state.userPreferenceSettings.preferences[key].filters;
  else return null;
};

export const selectGridColumns = (state, key) => {
  return state.userPreferenceSettings.preferences[key].columns;
};

export const getAutoPickupEnable = state => {
  return state.userPreferenceSettings.preferences.messaging.enableAutoPickup;
};

export const getEnableNotificationSound = state => {
  return state.userPreferenceSettings.preferences.messaging
    .enableNotificationSound;
};

export const getUserPreferenceSettings = payload => dispatch => {
  let userAPI = UserAPI.preferenceSettings(payload.params.id);
  return ApiServices.getWithParams(userAPI, payload)
    .then(async res => {
      let { data } = res;
      if (data?.success) {
        const preferences = tryParseJson(data.data);
        if (preferences) await dispatch(setPreferencesToState(preferences));
        return Promise.resolve(data);
      }
      return Promise.reject(res);
    })
    .catch(error => {
      return Promise.reject(error);
    });
};

export const updateUserPreferences = payload => {
  let userAPI = UserAPI.preferenceSettings(payload.userId);
  return ApiServices.put(userAPI, payload);
};

export default userPreferenceSettings.reducer;
